import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'

interface RollTextProps {
    value: string
    className?: string
    charClassName?: string
    stagger?: number
}

// Swaps a value character by character: the old one rolls up and out while the
// new one rolls in from below, like a split-flap board.
export const RollText: FC<RollTextProps> = ({ value, className, charClassName, stagger = 0.045 }): ReactNode => {
    const reduce = useReducedMotion()
    const chars = Array.from(value)

    return (
        <span className={cn('relative inline-flex overflow-hidden', className)}>
            <span className='sr-only' aria-live='polite'>
                {value}
            </span>
            <AnimatePresence mode='popLayout' initial={false}>
                <motion.span key={value} aria-hidden className='inline-flex'>
                    {chars.map((char, index) => (
                        <motion.span
                            key={`${value}-${index}`}
                            className={cn('inline-block', charClassName)}
                            initial={reduce ? { opacity: 0 } : { y: '100%' }}
                            animate={reduce ? { opacity: 1 } : { y: '0%' }}
                            exit={reduce ? { opacity: 0 } : { y: '-100%' }}
                            transition={{ duration: 0.75, ease: EASE_OUT_EXPO, delay: index * stagger }}
                        >
                            {char === ' ' ? ' ' : char}
                        </motion.span>
                    ))}
                </motion.span>
            </AnimatePresence>
        </span>
    )
}

interface CountUpProps {
    to: number
    from?: number
    decimals?: number
    prefix?: string
    suffix?: string
    duration?: number
    className?: string
}

// Counts up once when it scrolls into view. The server renders the final value.
export const CountUp: FC<CountUpProps> = ({ to, from = 0, decimals = 0, prefix = '', suffix = '', duration = 1.8, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
    const reduce = useReducedMotion()
    const [display, setDisplay] = useState(to)
    const started = useRef(false)

    useEffect(() => {
        if (!reduce && !started.current) setDisplay(from)
    }, [reduce, from])

    useEffect(() => {
        if (!inView || started.current || reduce) return

        started.current = true

        let frame = 0
        const start = performance.now()

        const tick = (now: number) => {
            const progress = Math.min(1, (now - start) / (duration * 1000))
            const eased = 1 - Math.pow(1 - progress, 4)

            setDisplay(from + (to - from) * eased)

            if (progress < 1) frame = requestAnimationFrame(tick)
        }

        setDisplay(from)
        frame = requestAnimationFrame(tick)

        return () => {
            cancelAnimationFrame(frame)
        }
    }, [inView, reduce, from, to, duration])

    return (
        <span ref={ref} className={cn('tabular-nums', className)}>
            {prefix}
            {display.toFixed(decimals)}
            {suffix}
        </span>
    )
}
